import * as Sentry from '@sentry/react'
import { ClerkFailed } from '@clerk/clerk-react'
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router'
import { Userpilot } from 'userpilot'

import { LocalizedClerkProvider } from '@/components/LocalizedClerkProvider.tsx'
import { ClerkFailedPage } from '@/components/ClerkFailedPage.tsx'
import UnleashProvider from '@/components/UnleashProvider.tsx'
import { AppVersionProvider } from '@/contexts/AppVersionContext.tsx'
import { hasSignInTicket, scrubSignInTickets } from '@/lib/sentryTicketScrub'

import App from './App.tsx'

import './i18n'
import './index.css'
import 'react-phone-number-input/style.css'

Userpilot.initialize(import.meta.env.VITE_USERPILOT_API_KEY)

// Only initialize Sentry in non-development environments
if (import.meta.env.VITE_ENVIRONMENT !== 'development') {
  const replay = Sentry.replayIntegration({
    maskAllInputs: true,
    maskAllText: true,
    blockAllMedia: false,
    beforeAddRecordingEvent: scrubSignInTickets,
  })
  // The replay Meta event records the raw page URL and skips beforeAddRecordingEvent,
  // so Replay starts only once the sign-in ticket has left the URL.
  const startReplayNow = !hasSignInTicket(window.location.href)
  Sentry.init({
    dsn: 'https://ff6738da7d27a1528002b1fb4b88feae@o4509532168978432.ingest.us.sentry.io/4509532218851328',
    sendDefaultPii: true,
    environment: import.meta.env.VITE_ENVIRONMENT,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(startReplayNow ? [replay] : []),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === 'production' ? 0.1 : 1.0, // Adjust sample rate for production,
  })
  // An event processor, unlike beforeSend, also reaches replay events.
  Sentry.getGlobalScope().addEventProcessor((event) => scrubSignInTickets(event))
  if (!startReplayNow) {
    const timer = window.setInterval(() => {
      if (!hasSignInTicket(window.location.href)) {
        window.clearInterval(timer)
        Sentry.addIntegration(replay)
      }
    }, 500)
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LocalizedClerkProvider>
      <ClerkFailed>
        <ClerkFailedPage />
      </ClerkFailed>
      <UnleashProvider>
        <AppVersionProvider>
          <App />
        </AppVersionProvider>
      </UnleashProvider>
    </LocalizedClerkProvider>
  </StrictMode>
)
